import { useState } from 'react';
import { usePlayerStore } from '@/state/playerStore';
import styles from './PracticePanel.module.css';

const pct = (rate: number) => Math.round(rate * 100);

/**
 * Practice tools (F3.6 count-in, F3.7 metronome, F3.8 chord synth, F9.1 speed trainer).
 * Shown as a popover above the transport bar.
 */
export function PracticePanel() {
  const {
    metronome,
    metronomeVolume,
    countIn,
    synth,
    synthVolume,
    trainer,
    loop,
    setPractice,
    setTrainer,
  } = usePlayerStore();
  const [plan, setPlan] = useState({ start: 70, target: 100, step: 5 });

  const startTrainer = () => {
    const start = Math.min(plan.start, plan.target);
    setTrainer({ start: start / 100, target: plan.target / 100, step: plan.step / 100 });
  };

  return (
    <div role="dialog" aria-label="Practice tools" className={styles.panel}>
      <span className={styles.title}>Practice</span>

      <div className={styles.row}>
        <label className={styles.toggle}>
          <input
            type="checkbox"
            checked={metronome}
            onChange={(e) => {
              setPractice({ metronome: e.target.checked });
            }}
          />
          Metronome
        </label>
        <input
          type="range"
          min={0}
          max={100}
          value={pct(metronomeVolume)}
          aria-label="Metronome volume"
          onChange={(e) => {
            setPractice({ metronomeVolume: Number(e.target.value) / 100 });
          }}
        />
      </div>

      <label className={styles.toggle}>
        <input
          type="checkbox"
          checked={countIn}
          onChange={(e) => {
            setPractice({ countIn: e.target.checked });
          }}
        />
        Count in one bar before playing
      </label>

      <div className={styles.row}>
        <label className={styles.toggle}>
          <input
            type="checkbox"
            checked={synth}
            onChange={(e) => {
              setPractice({ synth: e.target.checked });
            }}
          />
          Play the chords
        </label>
        <input
          type="range"
          min={0}
          max={100}
          value={pct(synthVolume)}
          aria-label="Chord sound volume"
          onChange={(e) => {
            setPractice({ synthVolume: Number(e.target.value) / 100 });
          }}
        />
      </div>
      <p className={styles.hint}>Mute the track to hear the chords alone.</p>

      <div className={styles.trainer}>
        <span className={styles.subtitle}>Speed trainer</span>
        {loop ? (
          <>
            <div className={styles.speeds}>
              {(
                [
                  ['start', 'Start'],
                  ['target', 'Target'],
                  ['step', 'Step'],
                ] as const
              ).map(([key, label]) => (
                <label key={key} className={styles.speed}>
                  {label}
                  <input
                    type="number"
                    min={key === 'step' ? 1 : 50}
                    max={key === 'step' ? 20 : 150}
                    step={key === 'step' ? 1 : 5}
                    value={plan[key]}
                    disabled={trainer !== null}
                    onChange={(e) => {
                      setPlan({ ...plan, [key]: Number(e.target.value) });
                    }}
                  />
                  %
                </label>
              ))}
            </div>
            <button
              type="button"
              className={styles.button}
              aria-pressed={trainer !== null}
              onClick={() => {
                if (trainer) setTrainer(null);
                else startTrainer();
              }}
            >
              {trainer ? 'Stop trainer' : 'Start trainer'}
            </button>
            <p className={styles.hint}>
              Each pass through the loop gets {plan.step}% faster, up to {plan.target}%.
            </p>
          </>
        ) : (
          <p className={styles.hint}>Turn on a loop first; the trainer speeds it up each pass.</p>
        )}
      </div>
    </div>
  );
}
